/* eslint-disable no-param-reassign */
import React, { useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Note } from '@annote/types';
import { Card } from 'src/components/Cards/Card';
import { LinkPort, BacklinkPort } from 'src/components/Cards/Port';
import { ShownIdsContext } from 'src/context/ShownContext';
import { annoteIndex } from 'src/context/annoteIndex';
import HiddenCards from 'src/components/Cards/HiddenCard';
import { CardDropzone } from 'src/components/Cards/CardDropzone';
import { PortRefMap } from 'src/helpers/ports';
import { ShownTree } from '../types';
import { CardSpacer } from './CardSpacer';

export interface CardTreeProps {
  note: Note;
  shownTree: ShownTree;
  setPortRefMap: React.Dispatch<React.SetStateAction<PortRefMap>>;
  parent?: Note;
  parentPortRef?: React.RefObject<HTMLDivElement>;
}

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  overflow: visible;
  padding: 0;
  padding-left: 0.35rem;
  margin-top: -4px;
`;

const CardTreeWrapper = styled.div`
  position: relative;
  display: flex;
  padding-left: 3.5rem;
  pointer-events: auto;
  flex: 1;
  width: fit-content;
`;

const TreePadding = styled.div`
  padding-left: 3.5rem;
  position: relative;
`;

const Branch = styled.div`
  position: relative;
`;

// const NonHoverableArea = styled.div<{ offset: number; isLeft: boolean }>`
//   width: ${(props) => props.offset * 1}rem;
//   height: 100%;
//   position: absolute;
//   top: 0;
//   left: ${(props) => (props.isLeft ? 'unset' : `${-1 * props.offset}rem`)};
//   right: ${(props) => (props.isLeft ? `${-1 * props.offset}rem` : 'unset')};
//   z-index: 11;
//   pointer-events: auto;
// `;

const CardWrapper = styled.div`
  position: relative;
  height: fit-content;

  &:hover {
    z-index: 1001;
  }
`;

export const CardTree: React.FC<CardTreeProps> = ({ note, shownTree, setPortRefMap, parent, parentPortRef }) => {
  const { showAafterB, hide, showAbeforeB, shownIds } = useContext(ShownIdsContext);

  const backlinkPortRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const linkPortRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;

  const { id } = note;
  const shownBranches = shownTree.shownBranches[id] || [];

  useEffect(() => {
    // Update portRefMap using the setPortRefMap function
    setPortRefMap((prevMap) => {
      const newPortRefMap: PortRefMap = { ...prevMap };
      newPortRefMap[`link-${note.id}`] = linkPortRef;
      newPortRefMap[`backlink-${note.id}`] = backlinkPortRef;
      return newPortRefMap;
    });

    return () => {
      // Remove the references from portRefMap when the component unmounts
      setPortRefMap((prevMap) => {
        const newMap = { ...prevMap };
        delete newMap[`backlink-${note.id}`];
        delete newMap[`link-${note.id}`];
        return newMap;
      });
    };
  }, [note]);

  // Calculate expanded IDs for port visibility (HiddenCards now handles its own logic)
  const { hiddenBacklinkIds, shownBacklinkIds } = useMemo(() => {
    const expandedBacklinks = annoteIndex.expandSkipOvers(note.backlinks || [], 'backlinks');
    const hiddenIds = expandedBacklinks.filter((backId) => !shownIds.includes(backId));
    const shownBLIds = expandedBacklinks.filter((backId) => shownIds.includes(backId));
    return { hiddenBacklinkIds: hiddenIds, shownBacklinkIds: shownBLIds };
  }, [note.backlinks, shownIds]);

  const { hiddenLinkIds, shownLinkIds } = useMemo(() => {
    const expandedLinks = annoteIndex.expandSkipOvers(note.links || [], 'links');
    const hiddenIds = expandedLinks.filter((linkId) => !shownIds.includes(linkId));
    const shownLIds = expandedLinks.filter((linkId) => shownIds.includes(linkId));
    return { hiddenLinkIds: hiddenIds, shownLinkIds: shownLIds };
  }, [note.links, shownIds]);

  const toggleLinks: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.stopPropagation();
      if (hiddenLinkIds.length) {
        // If there are hidden links, show them all
        showAbeforeB(hiddenLinkIds, id);
        return;
      }
      // Hide the currently shown links
      hide(shownLinkIds);
    },
    [hiddenLinkIds, shownLinkIds, id, showAbeforeB, hide],
  );

  const toggleBacklinks: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.stopPropagation();
      if (hiddenBacklinkIds.length) {
        // If there are hidden backlinks, show them all
        showAafterB(hiddenBacklinkIds, id);
        return;
      }
      // Hide the currently shown backlinks
      hide(shownBacklinkIds);
    },
    [hiddenBacklinkIds, shownBacklinkIds, id, showAafterB, hide],
  );

  const showLinkPort = useMemo(
    () =>
      // show the Port if there are shown links drawn, or hidden notes
      hiddenLinkIds.length > 0 || shownLinkIds.length > 0,
    [hiddenLinkIds, shownLinkIds],
  );

  const showBacklinkPort = useMemo(
    () =>
      // show the Port if there are shown backlinks drawn, or hidden notes
      hiddenBacklinkIds.length > 0 || shownBacklinkIds.length > 0,
    [hiddenBacklinkIds, shownBacklinkIds],
  );

  // const showBacklinksConnector = shownBranches.length > 0 || hiddenBackLinks.length > 0;

  return (
    <CardTreeWrapper>
      <div>
        <CardWrapper className="card-wrapper">
          <HiddenCards parentNoteId={note.id} kind="backlinks" relationIds={note.backlinks || []} />
          <HiddenCards parentNoteId={note.id} kind="links" relationIds={note.links || []} />

          <LinkPort ref={linkPortRef} isShown={showLinkPort} onClick={toggleLinks} />
          <Card key={note.id} note={note} />
          <BacklinkPort ref={backlinkPortRef} isShown={showBacklinkPort} onClick={toggleBacklinks} />
        </CardWrapper>

        {
          // TODO - need the previous backlinkPortRef not the current one...
        }
        {/* This is the spacer and dropzone beneath a card */}
        <CardSpacer linkTo={parent || null} afterId={note.id} backlinkPortRef={parentPortRef} />
        <CardDropzone linkTo={parent || null} afterId={note.id} />
      </div>
      <CardList>
        <TreePadding>
          <CardDropzone linkTo={note} afterId={note.id} />
          {/* This is the Top spacer and dropzone for a new tree */}
          <CardSpacer linkTo={note} afterId={note.id} backlinkPortRef={backlinkPortRef} />
        </TreePadding>
        {shownBranches.map((branch) => (
          <Branch key={`tree-${branch.id}`}>
            <CardTree
              note={branch}
              shownTree={shownTree}
              setPortRefMap={setPortRefMap}
              parent={note}
              parentPortRef={backlinkPortRef}
            />
            <CardDropzone linkTo={note} afterId={branch.id} />
          </Branch>
        ))}
      </CardList>
    </CardTreeWrapper>
  );
};
